import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import Controls from "../components/Controls";
import Progress from "../components/Progress";
import RoomHeader from "../components/RoomHeader";
import { useTranslation } from "../components/Localization";
import { usePageGame } from "../hooks/usePageGame";
import { saveGameScore } from "../services/gameScores";

const WIDTH = 205;
const HEIGHT = 331;

type RestorationGamePageProps = { variant: 1 | 2 };

export function restorationScore(percentage: number) {
  if (percentage < 45) return 0;
  if (percentage < 55) return 5;
  if (percentage < 65) return 6;
  if (percentage < 75) return 7;
  if (percentage < 85) return 8;
  if (percentage < 95) return 9;
  return 10;
}

export default function RestorationGamePage({ variant }: RestorationGamePageProps) {
  const { room, t } = useTranslation();
  const { game } = usePageGame("talalo", "scratch_reveal");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const measurementCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const previousPoint = useRef<{ x: number; y: number } | null>(null);
  const lastMeasurement = useRef(0);
  const [revealed, setRevealed] = useState(false);
  const [restoredPercentage, setRestoredPercentage] = useState(0);
  const [score, setScore] = useState(0);
  const roundImages = (game?.items || [])
    .filter((item) => item.roundIndex === variant - 1 && item.kind === "image" && item.url)
    .sort((first, second) => first.sortOrder - second.sortOrder);
  const damagedImage = roundImages[0]?.url || "";
  const restoredImage = roundImages[roundImages.length - 1]?.url || "";

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context || !damagedImage) return;

    setRevealed(false);
    setRestoredPercentage(0);
    setScore(0);
    context.clearRect(0, 0, WIDTH, HEIGHT);
    const measurementCanvas = document.createElement("canvas");
    measurementCanvas.width = WIDTH;
    measurementCanvas.height = HEIGHT;
    const measurementContext = measurementCanvas.getContext("2d");
    if (measurementContext) {
      measurementContext.fillStyle = "#000";
      measurementContext.fillRect(0, 0, WIDTH, HEIGHT);
    }
    measurementCanvasRef.current = measurementCanvas;
    const image = new Image();
    image.src = damagedImage;
    image.onload = () => {
      const scale = Math.max(WIDTH / image.width, HEIGHT / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      context.globalCompositeOperation = "source-over";
      context.drawImage(image, (WIDTH - width) / 2, (HEIGHT - height) / 2, width, height);
    };
  }, [damagedImage]);

  const measureRestoration = (force = false) => {
    const now = performance.now();
    if (!force && now - lastMeasurement.current < 100) return;
    lastMeasurement.current = now;

    const measurementContext = measurementCanvasRef.current?.getContext("2d");
    if (!measurementContext) return;
    const pixels = measurementContext.getImageData(0, 0, WIDTH, HEIGHT).data;
    let removedPixels = 0;
    for (let pixel = 0; pixel < WIDTH * HEIGHT; pixel += 1) {
      if (pixels[pixel * 4 + 3] <= 32) removedPixels += 1;
    }

    const percentage = Math.min(100, Math.round((removedPixels / (WIDTH * HEIGHT)) * 100));
    const nextScore = restorationScore(percentage);
    setRestoredPercentage(percentage);
    setScore(nextScore);
    setRevealed(percentage >= 45);

    if (nextScore > 0) {
      saveGameScore(variant === 1 ? "restorationOne" : "restorationTwo", nextScore);
    }
  };

  const pointFromEvent = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * WIDTH,
      y: ((event.clientY - bounds.top) / bounds.height) * HEIGHT,
    };
  };

  const eraseTo = (point: { x: number; y: number }) => {
    const context = canvasRef.current?.getContext("2d");
    const measurementContext = measurementCanvasRef.current?.getContext("2d");
    if (!context || !measurementContext) return;
    const previous = previousPoint.current || point;
    [context, measurementContext].forEach((drawingContext) => {
      drawingContext.save();
      drawingContext.globalCompositeOperation = "destination-out";
      drawingContext.lineWidth = 38;
      drawingContext.lineCap = "round";
      drawingContext.lineJoin = "round";
      drawingContext.beginPath();
      drawingContext.moveTo(previous.x, previous.y);
      drawingContext.lineTo(point.x, point.y);
      drawingContext.stroke();
      drawingContext.restore();
    });
    previousPoint.current = point;
  };

  const startErasing = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointFromEvent(event);
    previousPoint.current = point;
    eraseTo(point);
  };

  const continueErasing = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    eraseTo(pointFromEvent(event));
    measureRestoration();
  };

  const finishErasing = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    drawing.current = false;
    previousPoint.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    measureRestoration(true);
  };

  return (
    <main className="screen activity restoration-page">
      <RoomHeader roomId="dining" />
      <p className="instruction">{room("dining")?.tasks.restoration}</p>
      <div className={`restore-area ${revealed ? "is-restored" : ""}`}>
        {restoredImage && <img className="restored-image" src={restoredImage} alt={t("accessibility.restoredPainting", { variant })} />}
        <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} onPointerDown={startErasing} onPointerMove={continueErasing} onPointerUp={finishErasing} onPointerCancel={finishErasing} aria-label={t("accessibility.damagedPainting")} />
      </div>
      <div className="restoration-status" aria-live="polite">
        <span>{t("score.restored", { percentage: restoredPercentage })}</span>
        <strong>{t("score.gameResult", { score, max: 10 })}</strong>
      </div>
    
      <Progress step={variant === 1 ? 2 : 3} total={4} icon="center-down" />
      <Controls backTo={variant === 1 ? "/talalo/parositas" : "/talalo/restauralas"} nextTo={variant === 1 ? "/talalo/restauralas-2" : "/talalo/eredmeny"} check={!revealed} nextDisabled={!revealed} />
    </main>
  );
}
